import * as ko from "knockout";
import * as Global from "../common/global";
import * as UI from "../common/ui";
import * as DocumentLibraryData from "../data/documentLibraryData";
import { IApplication } from "../interfaces/application";
import { IController } from "../interfaces/controller";
import * as Data from "../interfaces/data";
import { IAppGlobal } from "../interfaces/user";

interface ICustomerDocumentObservable {
    ID: KnockoutObservable<number>;
    StorageKey: KnockoutObservable<string>;
    IsActive: KnockoutObservable<boolean>;
    Description: KnockoutObservable<string>;
    FileName: KnockoutObservable<string>;
    TempFileKey: KnockoutObservable<string>;
    DocumentInUse: KnockoutObservable<boolean>;
}

interface IDocumentLibraryViewModel {
    Documents: KnockoutObservableArray<ICustomerDocumentObservable>;
    AllowedExtensions: string;
    DocumentID: KnockoutObservable<number>;
    FileName: KnockoutObservable<string>;
    TempFileKey: KnockoutObservable<string>;
    DownloadUrl: string;
    replaceDocument: (doc: ICustomerDocumentObservable) => void;
    deleteDocument: (doc: ICustomerDocumentObservable) => void;
    closeUploadModal: () => void;
}

export class DocumentLibraryController implements IController {
    // TODO - read extensions from DocumentFileTypeAllowed table

    private allowedExtensions: string[] = this.ValidFiles();
    private uploadDialog: JQuery;
    private container: JQuery;
    private viewModel: IDocumentLibraryViewModel;
    private uploadControl: kendo.ui.Upload;
    private fieldsOnLoad: string = "";

    constructor() {
        const app: IApplication = Global.app();
        this.viewModel = {
            Documents: ko.observableArray(),
            DocumentID: ko.observable(),
            FileName: ko.observable(),
            TempFileKey: ko.observable(),
            DownloadUrl: app.webServiceBaseURL + "/api/DocumentLibrary/Download/",
            AllowedExtensions: this.allowedExtensions.join(", "),
            closeUploadModal: () => {
                this.uploadDialog.modal("hide");
            },
            replaceDocument: (doc: ICustomerDocumentObservable) => {
                this.viewModel.DocumentID(doc.ID());
                this.uploadDialog.modal("show");
            },
            deleteDocument: (doc: ICustomerDocumentObservable) => {

                let msg = "Are you sure you want to delete this document?";

                if (doc.DocumentInUse()) {
                    msg += "\n\nWarning: This document is currently linked to at least one entity."
                        + "\nIf you proceed this document will be removed from any linked templates, tasks and targets.";
                }

                UI.showConfirmDialog(msg, () => {
                    if (doc.ID()) {
                        // This document is already in the db so soft-delete
                        doc.IsActive(false);
                    } else {
                        // This document hasn't been saved to the db yet so remove it
                        this.viewModel.Documents.remove(doc);
                    }
                });
            },
        };
    }
    public ValidFiles(): string [] {
        const appGlobal: IAppGlobal = Global.app().global;
        const allowedExtensions = [];
        for (let i = 0; i < appGlobal.ValidFileExtensions.length; ++i) {
            allowedExtensions[i] = appGlobal.ValidFileExtensions[i].DocumentTypeExtension;
        }
        return allowedExtensions;
    }

    public save(): JQueryPromise<any> {
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(this.container);

        DocumentLibraryData.Save(this.createCustomerDocumentList())
            .done((response: Data.IResponse<DocumentLibraryData.ICustomerDocument[]>) => {
                if (response.Success) {
                    this.loadViewModel(response.Payload);
                    deferred.resolve();
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                    deferred.reject();
                }
            })
            .always(() => {
                UI.hideWaitIndicator(this.container, waitContainer);
            });

        return deferred.promise();
    }

    public add(): void {
        this.viewModel.DocumentID(null);
        this.viewModel.FileName(null);
        this.viewModel.TempFileKey(null);
        this.uploadControl.clearAllFiles();
        this.uploadDialog.modal("show");
    }

    public render(container: JQuery<HTMLElement>): JQueryPromise<any> {
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        const app: IApplication = Global.app();
        this.container = container;
        this.uploadDialog = this.container.find("div[data-id='uploadDocumentDialog']");

        this.uploadControl = this.container.find("input[data-id='uploadDocumentControl']").kendoUpload({
            async: {
                saveUrl: "save",
                autoUpload: true,
            },
            validation: {
                allowedExtensions: this.allowedExtensions,
            },
            upload: (e: kendo.ui.UploadUploadEvent) => {
                if (e.sender.options.async) {
                    e.sender.options.async.saveUrl = app.webServiceBaseURL + "/api/DocumentLibrary/Upload";
                }
            },
            success: (e: kendo.ui.UploadSuccessEvent) => {
                this.viewModel.FileName(e.response.FileName);
                this.viewModel.TempFileKey(e.response.TempFileKey);

                if (this.viewModel.TempFileKey()) {

                    if (this.viewModel.DocumentID()) {
                        // replacing an existing document
                        this.viewModel.Documents().forEach((doc: ICustomerDocumentObservable) => {
                            if (doc.ID() === this.viewModel.DocumentID()) {
                                doc.TempFileKey(this.viewModel.TempFileKey());
                                doc.FileName(this.viewModel.FileName());
                            }
                        });

                    } else {
                        // uploading a new document
                        this.viewModel.Documents.push({
                            ID: ko.observable(),
                            Description: ko.observable(),
                            FileName: ko.observable(this.viewModel.FileName()),
                            TempFileKey: ko.observable(this.viewModel.TempFileKey()),
                            IsActive: ko.observable(true),
                            StorageKey: ko.observable(),
                            DocumentInUse: ko.observable(false),
                        });
                    }
                }

            },
            multiple: false,
        }).data("kendoUpload");

        ko.applyBindings(this.viewModel, this.container[0]);

        deferred.resolve();
        return deferred.promise();
    }

    public load(params: any[]): JQueryPromise<any> {
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(this.container);

        DocumentLibraryData.GetAllDocuments()
            .done((response: Data.IResponse<DocumentLibraryData.ICustomerDocument[]>) => {
                if (response.Success) {
                    this.loadViewModel(response.Payload);
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                }
            })
            .always(() => {
                UI.hideWaitIndicator(this.container, waitContainer);
            });

        deferred.resolve();
        return deferred.promise();
    }

    public haveFieldsChanged(): boolean {
        const fieldValues: string = JSON.stringify(this.createCustomerDocumentList());
        return fieldValues !== this.fieldsOnLoad;
    }

    public appRouterID(params: any[]): string {
        return "DocumentLibrary";
    }

    public title(): string {
        return "Document Library";
    }

    public subTitle(): string {
        return "";
    }

    private createCustomerDocumentList(): DocumentLibraryData.ICustomerDocument[] {
        const docList: DocumentLibraryData.ICustomerDocument[] = [];

        this.viewModel.Documents().forEach((docObservable) => {
            docList.push({
                ID: docObservable.ID(),
                StorageKey: docObservable.StorageKey(),
                IsActive: docObservable.IsActive(),
                Description: docObservable.Description(),
                FileName: docObservable.FileName(),
                TempFileKey: docObservable.TempFileKey(),
                DocumentInUse: docObservable.DocumentInUse(),
            });
        });

        return docList;
    }

    private loadViewModel(docs: DocumentLibraryData.ICustomerDocument[]) {

        this.fieldsOnLoad = JSON.stringify(docs);

        this.viewModel.Documents.removeAll();

        docs.forEach((doc: DocumentLibraryData.ICustomerDocument) => {
            this.viewModel.Documents.push({
                ID: ko.observable(doc.ID),
                DocumentInUse: ko.observable(doc.DocumentInUse),
                StorageKey: ko.observable(doc.StorageKey),
                IsActive: ko.observable(doc.IsActive),
                Description: ko.observable(doc.Description),
                FileName: ko.observable(doc.FileName),
                TempFileKey: ko.observable(doc.TempFileKey),
            });
        });
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/controllers/documentLibrary.ts